import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IconBell, IconX } from "@tabler/icons-react";
import { clsx } from "clsx";
import { Heading, Text } from "../typography";
import { Button } from "../button";
import { ShimmerLoader } from "../shimmer";
import { EmptyState } from "../empty-state";
import { useMyInbox } from "../../../modules/inbox/hooks/useMyInbox";
import { useInboxMutations } from "../../../modules/inbox/hooks/useInboxMutations";
import { resolveNotificationLink } from "../../../modules/inbox/resolve-notification-link";
import { useMePartner } from "../../../swr";
import type { NotificationDto } from "../../../api";

const DROPDOWN_PAGE_SIZE = 5;

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
}

export function NotificationCenter({ isOpen, onClose, anchorRef }: NotificationCenterProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, error } = useMyInbox({ page: 1, pageSize: DROPDOWN_PAGE_SIZE });
  const { markRead, markAllRead } = useInboxMutations();
  const { isSeller, isBuyer } = useMePartner();

  const notifications = (data?.data ?? []) as NotificationDto[];
  const hasUnread = notifications.some((n) => n.readAt === null);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  const handleRowClick = async (n: NotificationDto) => {
    if (n.readAt === null) {
      await markRead(n.id).catch(() => {});
    }
    const target = resolveNotificationLink(n, { isSeller, isBuyer });
    if (target) {
      onClose();
      const current = `${location.pathname}${location.search}${location.hash}`;
      if (target !== current) navigate(target);
    }
  };

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Notification center"
      className={clsx(
        "absolute right-0 top-[calc(100%+12px)] z-50 w-[460px] max-w-[calc(100vw-2rem)]",
        "overflow-hidden rounded-2xl border border-borderDefault bg-bgWhite",
        "shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.15),0px_8px_10px_2px_rgba(0,0,0,0.1)]",
      )}
    >
      <div className="flex items-center justify-between border-b border-borderDefault px-4 py-3">
        <Heading variant="h3" tone="dark">Notification Center</Heading>
        <div className="flex items-center gap-2">
          {hasUnread && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void markAllRead().catch(() => {})}
            >
              Mark all read
            </Button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close notifications"
            className="inline-flex size-6 items-center justify-center text-textDark hover:text-textBody"
          >
            <IconX size={20} />
          </button>
        </div>
      </div>

      <div className="flex flex-col">
        {isLoading ? (
          <div className="px-4 py-6">
            <ShimmerLoader message="Fetching your notifications…" />
          </div>
        ) : error ? (
          <div className="px-4 py-8 text-center">
            <Text variant="bodySm" tone="muted">Couldn't load notifications. Please try again.</Text>
          </div>
        ) : notifications.length === 0 ? (
          <EmptyState
            icon={IconBell}
            title="You're all caught up"
            size="sm"
          />
        ) : (
          notifications.map((n) => (
            <button
              type="button"
              key={n.id}
              onClick={() => void handleRowClick(n)}
              className={clsx(
                "flex flex-col gap-1 px-4 py-3 text-left border-b border-borderDefault last:border-b-0",
                "transition-colors hover:bg-bgLight",
                n.readAt === null && "bg-accentGreenTintBg",
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                  <span
                    className={clsx(
                      "mt-1.5 size-2 shrink-0 rounded-full",
                      n.readAt === null ? "bg-accentGreenDark" : "bg-transparent",
                    )}
                  />
                  <Text variant="bodySmBold" tone="dark">{n.title}</Text>
                </div>
                <Text variant="bodyXs" tone="muted" className="shrink-0">
                  {formatRelativeTime(n.createdAt)}
                </Text>
              </div>
              <Text variant="bodySm" tone="body" className="pl-4">{n.body}</Text>
            </button>
          ))
        )}
      </div>

      <div className="border-t border-borderDefault px-4 py-3 text-center">
        <Button
          variant="ghost"
          size="sm"
          fullWidth
          className="!text-textDark"
          onClick={() => {
            onClose();
            navigate("/inbox");
          }}
        >
          View all notifications
        </Button>
      </div>
    </div>
  );
}

function formatRelativeTime(iso: string | Date): string {
  const date = typeof iso === "string" ? new Date(iso) : iso;
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default NotificationCenter;
